import { Component, OnInit } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { SortType } from "@app/enum/SortType";
import { DateService, DateUnit } from "@app/services/date.service";
import { DriverInfringementStatus, DriverTachoMode, IDriverTachoHours, ITachoDay, ITachoModePeriod, ITachoModeSummary, TachoService } from "@app/services/tacho.service";
import { TranslationService } from "@app/services/translation.service";
import "./tacho-compliance-page.component.scss";

@Component({
    selector: "tacho-compliance",
    template: require("./tacho-compliance-page.component.html")
})
export class TachoComplianceComponent implements OnInit {

    loading: boolean;
    sideBarLoading: boolean;

    currentDriverTachoHours: IDriverTachoHours[] = [];
    filterTabs: IFilterBarTab<IDriverTachoHours>[] = [];

    selectedDriver: IDriverTachoHours;
    selectedDriverTachoDays: ITachoDay[];

    modeSummary: ITachoModeSummary[];

    startDate: Date;
    endDate: Date;

    updateTimeCutOff: Date;

    tableColumns: IVTableColumn<IDriverTachoHours>[];
    tableRows: IVTableRow[];

    constructor(
        private readonly tachoService: TachoService,
        private readonly dateService: DateService,
        private readonly translationService: TranslationService,
        private readonly vTableService: VTableService
    ) {
    }

    ngOnInit() {
        this.createTabs();
        this.createTable();

        this.startDate = this.dateService.addOrSubtractTime(new Date(), -7, DateUnit.Day);
        this.endDate = this.dateService.addOrSubtractTime(new Date(), -1, DateUnit.Day);

        this.updateTimeCutOff = this.dateService.addOrSubtractTime(new Date(), -1, DateUnit.Day);

        this.updateData(true);
    }

    onFilterChanged(filterEvent: IAggregateFilterEvent<any>) {
        if (filterEvent.trigger == "date") {
            this.dateChanged(filterEvent.date);
        }
    }

    dateChanged(dates: Date[]) {

        this.startDate = dates[0];
        this.endDate = dates[1];

        this.loadTachoDataForDriver();
    }

    selectDriver(driver: IDriverTachoHours) {
        this.selectedDriver = driver;
    }

    loadTachoDataForDriver() {

        this.sideBarLoading = true;
        this.selectedDriverTachoDays = [];

        this.tachoService
            .getTachoDaysForDriver(this.selectedDriver.Driver.Id, this.startDate, this.endDate)
            .then(tachoDays => {
                this.selectedDriverTachoDays = tachoDays;

                this.modeSummary = this.tachoService.createModeSummaryMins(this.selectedDriverTachoDays);
            })
            .finally(() => {
                this.sideBarLoading = false;
            });
    }

    filterRenderable(summaries: ITachoModeSummary[]): ITachoModeSummary[] {
        return summaries?.filter(s => s.Mode !== DriverTachoMode.Future);
    }

    shouldShowTooltip(period: ITachoModePeriod): boolean {
        return period.Mode !== DriverTachoMode.Future;
    }

    shouldShowDuration(period: ITachoModePeriod): boolean {
        return period.Mode !== DriverTachoMode.Future && period.DurationPercentage >= 8;
    }

    // ---

    private updateData(showLoadingIndicator = false): angular.IPromise<any> {
        this.loading = showLoadingIndicator;

        return this.tachoService
            .getCurrentTachoForAllDrivers(true)
            .then((driverTachoHours) => {
                this.currentDriverTachoHours = driverTachoHours;
                this.tableRows = this.vTableService.createTableRows(this.currentDriverTachoHours, this.tableColumns);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createStatusTableHeader(),
                createCellFunction: tacho => this.vTableService.createStatusCell(this.getRowStatus(tacho))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TachoCompliancePage.DriverName" }),
                createCellFunction: tacho => this.vTableService.createTableCell(tacho.Driver.Name)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TachoCompliancePage.Warnings", minTableWidth: 800 }),
                createCellFunction: tacho => this.vTableService.createTableCell(tacho.DriverTachoDisplay.statusMessage)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TachoCompliancePage.ContinuousDrive", width: 140, sortType: SortType.Number }),
                createCellFunction: tacho => this.vTableService.createDurationCell(tacho.ContinuousDrive, DateUnit.Min)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TachoCompliancePage.BreakRestTaken", minTableWidth: 700, width: 100, sortType: SortType.Number }),
                createCellFunction: tacho => this.vTableService.createDurationCell(tacho.BreakRestTaken, DateUnit.Min)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TachoCompliancePage.DailyDrive", minTableWidth: 400, width: 100, sortType: SortType.Number }),
                createCellFunction: tacho => this.vTableService.createDurationCell(tacho.DailyDrive, DateUnit.Min)
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "TachoCompliancePage.WeeklyDrive", minTableWidth: 1100, width: 110, sortType: SortType.Number }),
                createCellFunction: tacho => this.vTableService.createDurationCell(tacho.WeeklyDrive, DateUnit.Min)
            },
            {
                header: this.vTableService.createDateTableHeader({ tKey: "TachoCompliancePage.Updated", minTableWidth: 500 }),
                createCellFunction: tacho => this.vTableService.createDateCell(tacho.EventTime, { dateTime: true, showErrorIfBeforeDate: this.updateTimeCutOff })
            }
        ]
    }

    private getRowStatus(tacho: IDriverTachoHours): VTableRowStatus {
        switch (tacho.DriverTachoDisplay.status) {
            case DriverInfringementStatus.Prewarning:
                return VTableRowStatus.Warning;

            case DriverInfringementStatus.Warning:
                return VTableRowStatus.Error;

            default:
                return VTableRowStatus.Info;
        }
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.createTabDefinition("All", (tachoHours) => true);
        this.createTabDefinition("Alerts", (tachoHours) => tachoHours.DriverTachoDisplay.status == DriverInfringementStatus.Warning);
        this.createTabDefinition("Warnings", (tachoHours) => tachoHours.DriverTachoDisplay.status == DriverInfringementStatus.Prewarning);
    }

    private createTabDefinition(translationString: string, callback: (tachoHours: IDriverTachoHours) => boolean): void {
        this.filterTabs.push({ title:/* this.translationService.t(translationString)*/translationString, predicate: callback });
    }
}
